/**
 * Shared runtime wiring consumed by RuntimeInner.
 * Centralizes useRemoteThreadListRuntime config so behavioral changes
 * only need to be made in one place.
 */

import { useCallback, useMemo, useRef } from 'react';
import {
  CompositeAttachmentAdapter,
  SimpleImageAttachmentAdapter,
  SimpleTextAttachmentAdapter,
  Suggestions,
  useAui,
} from '@assistant-ui/react';
import { SimplePDFAttachmentAdapter } from '@/client/components/assistant-ui/attachment';
import { useLangGraphRuntime } from '@assistant-ui/react-langgraph';
import type { LangChainMessage, LangGraphInterruptState } from '@assistant-ui/react-langgraph';
import {
  discoverActiveRunId,
  getThread,
  getThreadState,
  joinRunStream,
  markHasMetadataTitle,
  registerThreadRun,
  sendMessage,
  unregisterThreadRun,
  updateThreadMetadata,
} from '@/client/lib/chatApi';
import {
  createThreadListAdapter,
  type ThreadListAdapterOptions,
} from '@/client/lib/threadListAdapter';
import { sanitizeMessagesForAssistantUi } from '@/client/lib/messageUtils';
import { isBackgroundThreadMetadata } from '@/client/lib/backgroundThreads';
import { useRuntimeRefs } from '@/client/lib/useRuntimeRefs';
import { interceptStream, isAbortLikeError, abortableSleep } from '@/client/lib/streamInterceptor';
import { useJoinStreamSignal } from '@/client/lib/joinStreamSignal';
import type { SuggestionConfig } from '@/client/lib/suggestionsContext';

export type RuntimeCoreConfig = {
  assistantId: string;
  /** Thread-list adapter options (getMetadata is wired internally). */
  adapterOptions: Omit<ThreadListAdapterOptions, 'getMetadata'>;
  suggestions: SuggestionConfig[];
  /** Called before sendMessage on normal (non-join) sends. */
  onBeforeSend?: (externalId: string, messages: LangChainMessage[]) => void;
  /** Extra fields spread into sendMessage params (e.g. { pageUrl }). */
  getSendExtras?: () => { pageUrl?: string; agentMetadata?: Record<string, string> };
  /** Intercept a normal send and handle it out-of-band (e.g. resume an interrupted run). */
  interceptSend?: (params: {
    threadId: string;
    messages: LangChainMessage[];
    extras: { pageUrl?: string; agentMetadata?: Record<string, string> };
    cachedThreadState: {
      isBackgroundThread: boolean;
    } | null;
  }) => Promise<InterceptSendResult>;
  /** Console label for load errors. @default 'Failed to load thread state:' */
  loadErrorLabel?: string;
};

export type InterceptSendResult =
  | boolean
  | {
      handled: boolean;
      optimisticMessages?: LangChainMessage[];
      waitFor?: Promise<unknown>;
    };

type PendingOptimisticMessages = {
  baseMessageCount: number;
  optimisticHumanMessages: LangChainMessage[];
};

export function buildPolledThreadStateEvent(
  messages: LangChainMessage[],
  opts?: { assumeBackgroundThread?: boolean },
) {
  return {
    event: 'updates' as const,
    data: {
      messages: sanitizeMessagesForAssistantUi(messages, opts),
    },
  };
}

export function buildOptimisticBackgroundSendEvent(
  existingMessages: LangChainMessage[],
  outgoingMessages: LangChainMessage[],
  opts?: { assumeBackgroundThread?: boolean },
) {
  const humanMessages = outgoingMessages.filter((message) => message.type === 'human');
  if (humanMessages.length === 0) return null;

  return {
    event: 'updates' as const,
    data: {
      messages: sanitizeMessagesForAssistantUi([...existingMessages, ...humanMessages], opts),
    },
    nextMessages: [...existingMessages, ...humanMessages],
  };
}

function extractComparableHumanMessageText(message: LangChainMessage): string | null {
  if (message.type !== 'human') return null;
  if (typeof message.content === 'string') {
    const trimmed = message.content.trim();
    return trimmed ? trimmed : null;
  }
  if (!Array.isArray(message.content)) return null;

  const text = message.content
    .flatMap((part) => {
      if (typeof part === 'string') return [part];
      if (
        part &&
        typeof part === 'object' &&
        !Array.isArray(part) &&
        part.type === 'text' &&
        typeof part.text === 'string'
      ) {
        return [part.text];
      }
      return [];
    })
    .join('\n')
    .trim();

  return text ? text : null;
}

function hasAcknowledgedOptimisticHumanMessages(
  polledMessages: LangChainMessage[],
  pending: PendingOptimisticMessages,
): boolean {
  if (polledMessages.length <= pending.baseMessageCount) return false;

  let searchIndex = pending.baseMessageCount;
  for (const optimisticMessage of pending.optimisticHumanMessages) {
    const optimisticText = extractComparableHumanMessageText(optimisticMessage);
    let matchedIndex = -1;

    for (let index = searchIndex; index < polledMessages.length; index += 1) {
      const candidate = polledMessages[index];
      if (candidate.type !== 'human') continue;

      if (optimisticMessage.id && candidate.id && optimisticMessage.id === candidate.id) {
        matchedIndex = index;
        break;
      }

      if (optimisticText && extractComparableHumanMessageText(candidate) === optimisticText) {
        matchedIndex = index;
        break;
      }
    }

    if (matchedIndex === -1) return false;
    searchIndex = matchedIndex + 1;
  }

  return true;
}

export function reconcileOptimisticHumanMessages(
  polledMessages: LangChainMessage[],
  pending: PendingOptimisticMessages | null,
) {
  if (!pending || pending.optimisticHumanMessages.length === 0) {
    return {
      messages: polledMessages,
      pending: null,
    };
  }

  if (hasAcknowledgedOptimisticHumanMessages(polledMessages, pending)) {
    return {
      messages: polledMessages,
      pending: null,
    };
  }

  return {
    messages: [...polledMessages, ...pending.optimisticHumanMessages],
    pending,
  };
}

function extractInterruptList(
  state: Record<string, unknown> | null | undefined,
): LangGraphInterruptState[] {
  if (!state || typeof state !== 'object' || Array.isArray(state)) return [];

  const directInterrupts = Array.isArray(state.interrupts)
    ? (state.interrupts as LangGraphInterruptState[])
    : state.interrupts && typeof state.interrupts === 'object'
      ? Object.values(state.interrupts as Record<string, unknown>).flatMap((value) =>
          Array.isArray(value) ? value : [],
        )
      : [];
  if (directInterrupts.length > 0) return directInterrupts;

  const tasks = Array.isArray((state as { tasks?: unknown[] }).tasks)
    ? (state as { tasks: unknown[] }).tasks
    : [];
  return tasks.flatMap((task) => {
    const taskInterrupts =
      task &&
      typeof task === 'object' &&
      !Array.isArray(task) &&
      Array.isArray((task as { interrupts?: unknown[] }).interrupts)
        ? (task as { interrupts: unknown[] }).interrupts
        : [];
    return taskInterrupts as LangGraphInterruptState[];
  });
}

export function useRuntimeCore(config: RuntimeCoreConfig) {
  const {
    assistantId,
    adapterOptions,
    suggestions,
    onBeforeSend,
    getSendExtras,
    interceptSend,
    loadErrorLabel = 'Failed to load thread state:',
  } = config;

  const {
    assistantIdRef,
    setStreamingStatus,
    activeStreams,
    joinedStream,
    extractFromValues,
    takeDirtyFiles: takeDirtyFilesSnapshot,
    clearStateRefs,
    getConversationTitle,
    clearConversationTitle,
  } = useRuntimeRefs(assistantId);

  const { signalRef: joinSignalRef, setJoin, clearJoin } = useJoinStreamSignal();

  // Ref-backed metadata for thread creation, exposed via returned setter
  const metadataRef = useRef<Record<string, unknown> | undefined>(undefined);
  const rawThreadMessagesRef = useRef(new Map<string, LangChainMessage[]>());
  const pendingOptimisticMessagesRef = useRef(new Map<string, PendingOptimisticMessages>());
  const cachedThreadStateRef = useRef(new Map<string, { isBackgroundThread: boolean }>());
  const setThreadCreationMetadata = useCallback((meta: Record<string, unknown>) => {
    metadataRef.current = meta;
  }, []);

  // Keep callback refs current so the async stream closure never reads stale values
  const onBeforeSendRef = useRef(onBeforeSend);
  onBeforeSendRef.current = onBeforeSend;
  const getSendExtrasRef = useRef(getSendExtras);
  getSendExtrasRef.current = getSendExtras;
  const interceptSendRef = useRef(interceptSend);
  interceptSendRef.current = interceptSend;

  const adapter = useMemo(
    () =>
      createThreadListAdapter({
        ...adapterOptions,
        getMetadata: () => metadataRef.current,
      }),
    // Adapter options are stable per mount (components are keyed)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const aui = useAui({
    suggestions: Suggestions(suggestions),
  });

  const runtime = useLangGraphRuntime({
    unstable_threadListAdapter: adapter,
    unstable_allowCancellation: true,
    autoCancelPendingToolCalls: false,
    adapters: {
      attachments: new CompositeAttachmentAdapter([
        new SimpleImageAttachmentAdapter(),
        new SimpleTextAttachmentAdapter(),
        new SimplePDFAttachmentAdapter(),
      ]),
    },
    stream: async function* (messages, streamConfig) {
      const { externalId } = await streamConfig.initialize();
      if (!externalId) throw new Error('Thread not initialized');

      // Detect join-stream marker set by JoinActiveStream component
      const configurable = (
        streamConfig as {
          runConfig?: { configurable?: { _join?: string; _follow?: string } };
        }
      ).runConfig?.configurable;
      const joinThreadId = configurable?._join;
      const followThreadId = configurable?._follow;

      activeStreams.markActive(externalId);
      try {
        if (!joinThreadId && !followThreadId) {
          onBeforeSendRef.current?.(externalId, messages);
        }
        setStreamingStatus(externalId, null);
        let runId: string | null = null;
        const dirtyFiles = joinThreadId || followThreadId ? undefined : takeDirtyFilesSnapshot();
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { abortSignal, initialize: _, ...sendConfig } = streamConfig;
        const followActiveRun = async function* ({
          joined,
          initialDelayMs = 0,
        }: {
          joined: boolean;
          /** Delay before discovering the run, useful to let a newly created run register. */
          initialDelayMs?: number;
        }) {
          if (joined) {
            joinedStream.markJoined(externalId);
            setStreamingStatus(externalId, 'Following active run');
          } else {
            setStreamingStatus(externalId, 'Working');
          }
          try {
            if (initialDelayMs > 0) {
              await abortableSleep(initialDelayMs, abortSignal);
            }

            // Backfill the messages-so-far in one shot. joinStream below replays
            // events for streamResumable runs, but doing this up front gives us
            // an immediate render and runs extractFromValues for files/assets.
            const [threadRes, stateRes] = await Promise.allSettled([
              getThread(externalId),
              getThreadState(externalId),
            ]);
            const assumeBackgroundThread = Boolean(
              threadRes.status === 'fulfilled' &&
                isBackgroundThreadMetadata(threadRes.value.metadata),
            );
            cachedThreadStateRef.current.set(externalId, {
              isBackgroundThread: assumeBackgroundThread,
            });
            if (stateRes.status === 'fulfilled') {
              const vals = stateRes.value.values as {
                messages?: LangChainMessage[];
              };
              if (vals?.messages) {
                extractFromValues(stateRes.value.values, externalId);
                const reconciledMessages = reconcileOptimisticHumanMessages(
                  vals.messages,
                  pendingOptimisticMessagesRef.current.get(externalId) ?? null,
                );
                if (reconciledMessages.pending) {
                  pendingOptimisticMessagesRef.current.set(
                    externalId,
                    reconciledMessages.pending,
                  );
                } else {
                  pendingOptimisticMessagesRef.current.delete(externalId);
                }
                rawThreadMessagesRef.current.set(externalId, reconciledMessages.messages);
                yield buildPolledThreadStateEvent(reconciledMessages.messages, {
                  assumeBackgroundThread,
                });
              }
            }

            // Bail out early if the thread isn't actually running. Avoids
            // chasing a stale "busy" signal from `load()`.
            const stillBusy =
              threadRes.status === 'fulfilled' && threadRes.value.status === 'busy';
            if (!stillBusy) return;

            const activeRunId = await discoverActiveRunId(externalId);
            if (!activeRunId) return;

            registerThreadRun(externalId, activeRunId);
            try {
              const sse = await joinRunStream(externalId, activeRunId, abortSignal);
              for await (const event of interceptStream(sse, {
                threadId: externalId,
                extractFromValues,
                setStreamingStatus,
              })) {
                yield event;
              }
            } catch (err) {
              if (isAbortLikeError(err)) return;
              // 404 means the run finished between discoverActiveRunId() and
              // joinRunStream() — expected race, treat as a no-op since the
              // state pull above already rendered the final messages.
              if ((err as { status?: number })?.status === 404) return;
              throw err;
            } finally {
              unregisterThreadRun(externalId, activeRunId);
            }
          } finally {
            if (joined) {
              joinedStream.unmarkJoined(externalId);
            }
            setStreamingStatus(externalId, null);
          }
        };
        try {
          if (joinThreadId || followThreadId) {
            try {
              yield* followActiveRun({ joined: !!joinThreadId });
            } catch (err) {
              if (isAbortLikeError(err)) return;
              throw err;
            }
          } else {
            const extras = getSendExtrasRef.current?.() ?? {};
            const intercepted =
              (await interceptSendRef.current?.({
                threadId: externalId,
                messages,
                extras,
                cachedThreadState: cachedThreadStateRef.current.get(externalId) ?? null,
              })) ?? false;
            const interceptedHandled =
              typeof intercepted === 'boolean' ? intercepted : intercepted.handled;
            if (interceptedHandled) {
              const baseMessages = rawThreadMessagesRef.current.get(externalId) ?? [];
              const optimisticHumanMessages = (
                typeof intercepted === 'object' && intercepted.optimisticMessages
                  ? intercepted.optimisticMessages
                  : messages
              ).filter((message) => message.type === 'human');
              const optimisticEvent = buildOptimisticBackgroundSendEvent(
                baseMessages,
                typeof intercepted === 'object' && intercepted.optimisticMessages
                  ? intercepted.optimisticMessages
                  : messages,
                { assumeBackgroundThread: true },
              );
              if (optimisticEvent) {
                pendingOptimisticMessagesRef.current.set(externalId, {
                  baseMessageCount: baseMessages.length,
                  optimisticHumanMessages,
                });
                rawThreadMessagesRef.current.set(externalId, optimisticEvent.nextMessages);
                yield {
                  event: optimisticEvent.event,
                  data: optimisticEvent.data,
                };
              }
              if (typeof intercepted === 'object') {
                await intercepted.waitFor;
              }
              // Small delay to let the newly created run transition from
              // the previous thread status (e.g. interrupted → busy).
              yield* followActiveRun({ joined: false, initialDelayMs: 1500 });
              return;
            }
            const rawStream = await sendMessage({
              threadId: externalId,
              messages,
              assistantId: assistantIdRef.current,
              ...extras,
              files: dirtyFiles,
              signal: abortSignal,
              config: sendConfig,
              onRunCreated: ({ run_id }) => {
                runId = run_id;
                registerThreadRun(externalId, run_id);
              },
            });
            for await (const event of interceptStream(rawStream, {
              threadId: externalId,
              extractFromValues,
              setStreamingStatus,
            })) {
              yield event;
            }
          }
        } catch (err) {
          if (isAbortLikeError(err)) return;
          throw err;
        } finally {
          if (runId) {
            unregisterThreadRun(externalId, runId);
          }
        }
      } finally {
        activeStreams.markInactive(externalId);
        clearJoin();
        // Write-back conversation title from agent middleware to thread metadata.
        // Always update: the agent-generated title is authoritative and should
        // overwrite any fallback title that was prematurely backfilled.
        const title = getConversationTitle();
        clearConversationTitle();
        if (title) {
          markHasMetadataTitle(externalId);
          void updateThreadMetadata(externalId, { conversation_title: title }).catch(() => {});
        }
      }
    },
    load: async (externalId) => {
      try {
        // Fetch state and thread in parallel. allSettled ensures a getThread()
        // failure doesn't break message loading.
        const [stateResult, threadResult] = await Promise.allSettled([
          getThreadState(externalId),
          getThread(externalId),
        ]);
        if (stateResult.status === 'rejected') throw stateResult.reason;
        const state = stateResult.value;
        const messages = (state.values as { messages?: LangChainMessage[] }).messages ?? [];
        extractFromValues(state.values, externalId);
        const reconciledMessages = reconcileOptimisticHumanMessages(
          messages,
          pendingOptimisticMessagesRef.current.get(externalId) ?? null,
        );
        if (reconciledMessages.pending) {
          pendingOptimisticMessagesRef.current.set(externalId, reconciledMessages.pending);
        } else {
          pendingOptimisticMessagesRef.current.delete(externalId);
        }
        rawThreadMessagesRef.current.set(externalId, reconciledMessages.messages);

        // If the thread has an active run, signal for stream joining
        if (threadResult.status === 'fulfilled' && threadResult.value.status === 'busy') {
          setJoin(externalId);
        }

        cachedThreadStateRef.current.set(externalId, {
          isBackgroundThread: Boolean(
            threadResult.status === 'fulfilled' &&
              isBackgroundThreadMetadata(threadResult.value.metadata),
          ),
        });

        return {
          messages: sanitizeMessagesForAssistantUi(reconciledMessages.messages, {
            assumeBackgroundThread: Boolean(
              threadResult.status === 'fulfilled' &&
                isBackgroundThreadMetadata(threadResult.value.metadata),
            ),
          }),
          interrupts: extractInterruptList(state as unknown as Record<string, unknown>),
        };
      } catch (err) {
        console.error(loadErrorLabel, externalId, err);
        clearStateRefs();
        rawThreadMessagesRef.current.delete(externalId);
        pendingOptimisticMessagesRef.current.delete(externalId);
        cachedThreadStateRef.current.delete(externalId);
        return { messages: [] };
      }
    },
  });

  return {
    runtime,
    aui,
    joinSignalRef,
    setThreadCreationMetadata,
    suggestions,
    extractFromValues,
  };
}
